import apiCall, {endpoints} from './api';

/**
 * Service pour la gestion des boutiques
 * ✅ Utilise les vrais endpoints du CommonController et AdminController
 */
export const boutiqueService = {
  /**
   * ✅ Récupérer toutes les boutiques (Super Admin uniquement)
   * Utilise: GET /api/admin/boutiques
   */
  getAllBoutiques: async (filters = {}) => {
    try {
      const params = {};
      
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.search) params.search = filters.search;
      if (filters.page) params.page = filters.page;
      if (filters.size) params.size = filters.size;
      if (filters.sortBy) params.sortBy = filters.sortBy;
      if (filters.sortOrder) params.sortOrder = filters.sortOrder;

      console.log('🔍 Appel API getAllBoutiques avec params:', params);
      const response = await apiCall.get('/admin/boutiques', params);
      console.log('📥 Réponse getAllBoutiques:', response);
      
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des boutiques:', error);
      return {
        success: false,
        message: 'Erreur lors de la récupération des boutiques'
      };
    }
  },

  /**
   * ✅ Récupérer une boutique par ID
   * Utilise: GET /api/admin/boutiques/{id}
   */
  getBoutiqueById: async (boutiqueId) => {
    try {
      console.log('🏪 Récupération boutique par ID:', boutiqueId);
      const response = await apiCall.get(`/admin/boutiques/${boutiqueId}`);
      console.log('📥 Réponse getBoutiqueById:', response);
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération de la boutique:', error);
      return {
        success: false,
        message: 'Boutique introuvable'
      };
    }
  },

  /**
   * ✅ NOUVEAU: Récupérer les infos de la boutique de l'utilisateur connecté
   * Utilise: GET /api/common/boutique/info (du CommonController)
   */
  getCurrentBoutiqueInfo: async () => {
    try {
      console.log('🏪 Récupération info boutique actuelle...');
      
      // D'abord essayer l'endpoint commun
      let response = await apiCall.get('/common/boutique/info');
      
      if (response.success) {
        console.log('✅ Info boutique récupérée via common:', response.data);
        return response;
      }
      
      // Si erreur 403, essayer les endpoints manager/employee
      console.log('⚠️ Échec common, tentative manager...');
      response = await apiCall.get('/manager/boutique/info');
      
      if (response.success) {
        console.log('✅ Info boutique récupérée via manager:', response.data);
        return response;
      }
      
      // Dernier recours : employee
      console.log('⚠️ Échec manager, tentative employee...');
      response = await apiCall.get('/employee/boutique/info');
      
      if (response.success) {
        console.log('✅ Info boutique récupérée via employee:', response.data);
        return response;
      }
      
      return {
        success: false,
        message: 'Aucun endpoint boutique accessible'
      };
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des infos boutique:', error);
      return {
        success: false,
        message: 'Impossible de récupérer les informations de la boutique'
      };
    }
  },

  /**
   * ✅ Créer une nouvelle boutique avec gestionnaire
   * Utilise: POST /api/admin/boutiques
   */
  createBoutique: async (boutiqueData) => {
    try {
      console.log('🏪 Création boutique avec données:', boutiqueData);
      
      // Validation des données requises
      const requiredFields = ['name', 'address', 'phone', 'manager_data'];
      for (const field of requiredFields) {
        if (!boutiqueData[field]) {
          return {
            success: false,
            message: `Le champ ${field} est requis`
          };
        }
      }

      // Validation des données du gestionnaire
      const managerRequired = ['first_name', 'last_name', 'email', 'username', 'phone', 'password'];
      for (const field of managerRequired) {
        if (!boutiqueData.manager_data[field]) {
          return {
            success: false,
            message: `Le champ gestionnaire ${field} est requis`
          };
        }
      }

      const response = await apiCall.post('/admin/boutiques', boutiqueData);
      console.log('📥 Réponse createBoutique:', response);
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de la création de la boutique:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la création de la boutique'
      };
    }
  },

  /**
   * ✅ Mettre à jour une boutique
   * Utilise: PUT /api/admin/boutiques/{id}
   */
  updateBoutique: async (boutiqueId, updateData) => {
    try {
      console.log('🔄 Mise à jour boutique:', boutiqueId, updateData);
      const response = await apiCall.put(`/admin/boutiques/${boutiqueId}`, updateData);
      console.log('📥 Réponse updateBoutique:', response);
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour de la boutique:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de la mise à jour de la boutique'
      };
    }
  },

  /**
 * Upload d'un logo pour une boutique (basé sur l'upload d'images de produits)
 */
uploadBoutiqueLogo: async (boutiqueId, logoFile, onProgress) => {
  try {
    console.log('🏪 Upload logo pour boutique:', boutiqueId);
    console.log('📁 Fichier:', logoFile.name, `(${logoFile.size} bytes)`);
    
    // Validation côté client (même que pour les produits)
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(logoFile.type)) {
      return {
        success: false,
        message: 'Type de fichier non supporté. Utilisez JPG, PNG ou WebP'
      };
    }
    
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (logoFile.size > maxSize) {
      return {
        success: false,
        message: 'La taille du fichier ne doit pas dépasser 5MB'
      };
    }
    
    // Créer FormData
    const formData = new FormData();
    formData.append('logo', logoFile);
    
    console.log('📤 Envoi du logo...');
    
    // Appel API vers l'endpoint boutique (similaire aux produits)
    const response = await apiCall.upload(
      `/admin/boutiques/${boutiqueId}/upload-logo`,
      formData,
      onProgress
    );
    
    console.log('📥 Réponse upload logo:', response);
    
    if (response.success) {
      console.log('✅ Logo uploadé avec succès:', response.data);
      return {
        success: true,
        data: response.data, // URL du logo
        message: 'Logo uploadé avec succès'
      };
    } else {
      console.log('❌ Échec upload logo:', response.message);
      return response;
    }
    
  } catch (error) {
    console.error('❌ Erreur upload logo:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Erreur lors de l\'upload'
    };
  }
},

/**
 * Test d'upload de logo (pour debugging)
 */
testUploadLogo: async (file) => {
  try {
    console.log('🧪 Test d\'upload logo...');
    
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiCall.upload('/admin/test-logo-upload', formData);
    
    console.log('📋 Résultat test logo:', response);
    return response;
    
  } catch (error) {
    console.error('❌ Erreur test upload logo:', error);
    return {
      success: false,
      message: 'Erreur lors du test d\'upload'
    };
  }
},

/**
 * Corriger l'URL d'un logo (basé sur la correction des images de produits)
 */
fixLogoUrl: (logoUrl) => {
  if (!logoUrl) return null;
  
  console.log('🔧 Correction URL logo:', logoUrl);
  
  // Si l'URL commence par /api/uploads/, ajouter le serveur backend
  if (logoUrl.startsWith('/api/uploads/')) {
    const correctedUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8082'}${logoUrl}`;
    console.log('✅ URL logo corrigée (ajout serveur):', correctedUrl);
    return correctedUrl;
  }
  
  // Si l'URL pointe vers le serveur React par erreur, corriger
  if (logoUrl.includes('localhost:3000/api/uploads/')) {
    const correctedUrl = logoUrl.replace('localhost:3000/', 'localhost:8082/');
    console.log('✅ URL logo corrigée (changement port):', correctedUrl);
    return correctedUrl;
  }
  
  // Si c'est déjà une URL complète vers le bon serveur
  if (logoUrl.includes('/api/uploads/boutiques/')) {
    console.log('✅ URL logo déjà correcte:', logoUrl);
    return logoUrl;
  }
  
  // Si c'est juste un nom de fichier ou chemin relatif
  if (!logoUrl.startsWith('http')) {
    const correctedUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8082'}/api/uploads/boutiques/${logoUrl}`;
    console.log('✅ URL logo corrigée (chemin relatif):', correctedUrl);
    return correctedUrl;
  }
  
  console.log('⚠️ URL logo non modifiée:', logoUrl);
  return logoUrl;
},

/**
 * Hook pour tester une URL de logo
 */
testLogoUrl: async (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      console.log('✅ Test logo réussi:', url);
      resolve(true);
    };
    img.onerror = () => {
      console.error('❌ Test logo échoué:', url);
      resolve(false);
    };
    img.src = url;
  });
},

  /**
   * ✅ Archiver une boutique
   * Utilise: DELETE /api/admin/boutiques/{id}
   */
  archiveBoutique: async (boutiqueId) => {
    try {
      console.log('🗄️ Archivage boutique:', boutiqueId);
      const response = await apiCall.delete(`/admin/boutiques/${boutiqueId}`);
      console.log('📥 Réponse archiveBoutique:', response);
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de l\'archivage de la boutique:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur lors de l\'archivage de la boutique'
      };
    }
  },

  /**
   * ✅ Récupérer les statistiques d'une boutique
   * Utilise: GET /api/admin/boutiques/{id}/stats
   */
  getBoutiqueStats: async (boutiqueId) => {
    try {
      console.log('📊 Récupération stats boutique:', boutiqueId);
      const response = await apiCall.get(`/admin/boutiques/${boutiqueId}/stats`);
      console.log('📥 Réponse getBoutiqueStats:', response);
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des stats:', error);
      return {
        success: false,
        message: 'Erreur lors de la récupération des statistiques'
      };
    }
  },

  /**
   * ✅ Récupérer les utilisateurs d'une boutique
   * Utilise: GET /api/admin/boutiques/{id}/users
   */
  getBoutiqueUsers: async (boutiqueId, params = {}) => {
    try {
      console.log('👥 Récupération utilisateurs boutique:', boutiqueId);
      const response = await apiCall.get(`/admin/boutiques/${boutiqueId}/users`, params);
      console.log('📥 Réponse getBoutiqueUsers:', response);
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des utilisateurs:', error);
      return {
        success: false,
        message: 'Erreur lors de la récupération des utilisateurs'
      };
    }
  },

  /**
   * Helper: Valider les données de boutique
   */
  validateBoutiqueData: (data) => {
    const errors = {};

    if (!data.name?.trim()) {
      errors.name = 'Le nom est requis';
    }

    if (!data.address?.trim()) {
      errors.address = 'L\'adresse est requise';
    }

    if (!data.phone?.trim()) {
      errors.phone = 'Le téléphone est requis';
    }

    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = 'Format d\'email invalide';
    }

    if (data.primaryColor && !/^#[0-9A-F]{6}$/i.test(data.primaryColor)) {
      errors.primaryColor = 'Format de couleur invalide';
    }

    if (data.secondaryColor && !/^#[0-9A-F]{6}$/i.test(data.secondaryColor)) {
      errors.secondaryColor = 'Format de couleur invalide';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  /**
   * Helper: Valider les données du gestionnaire
   */
  validateManagerData: (managerData) => {
    const errors = {};

    if (!managerData.first_name?.trim()) {
      errors.first_name = 'Le prénom est requis';
    }

    if (!managerData.last_name?.trim()) {
      errors.last_name = 'Le nom est requis';
    }

    if (!managerData.email?.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(managerData.email)) {
      errors.email = 'Format d\'email invalide';
    }

    if (!managerData.username?.trim()) {
      errors.username = 'Le nom d\'utilisateur est requis';
    } else if (managerData.username.length < 3) {
      errors.username = 'Au moins 3 caractères requis';
    }

    if (!managerData.phone?.trim()) {
      errors.phone = 'Le téléphone est requis';
    }

    if (!managerData.password?.trim()) {
      errors.password = 'Le mot de passe est requis';
    } else if (managerData.password.length < 6) {
      errors.password = 'Au moins 6 caractères requis';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

    /**
   * Upload du logo de la boutique
   * @param {string} boutiqueId - ID de la boutique
   * @param {File} logoFile - Fichier image du logo
   * @param {Function} onProgress - Callback de progression (optionnel)
   * @returns {Promise<Object>} Boutique avec nouveau logo
   */
  uploadLogo: async (boutiqueId, logoFile, onProgress = null) => {
    try {
      console.log('🏪 Upload logo pour boutique:', boutiqueId);
      
      // Validation du fichier
      if (!logoFile || !(logoFile instanceof File)) {
        return {
          success: false,
          message: 'Fichier logo requis'
        };
      }

      // Validation du type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(logoFile.type)) {
        return {
          success: false,
          message: 'Format de fichier non supporté. Utilisez JPG, PNG ou WebP'
        };
      }

      // Validation de la taille (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (logoFile.size > maxSize) {
        return {
          success: false,
          message: 'Fichier trop volumineux (5MB maximum)'
        };
      }

      // Créer le FormData
      const formData = new FormData();
      formData.append('logo', logoFile);

      // Envoyer la requête avec callback de progression
      const response = await apiCall.upload(
        endpoints.admin.boutiques.uploadLogo(boutiqueId),
        formData,
        onProgress
      );

      if (response.success) {
        console.log('✅ Logo uploadé avec succès');
        return response;
      } else {
        console.error('❌ Erreur upload logo:', response.message);
        return response;
      }

    } catch (error) {
      console.error('❌ Erreur lors de l\'upload du logo:', error);
      return {
        success: false,
        message: 'Erreur lors de l\'upload du logo'
      };
    }
  },

  /**
   * Mettre à jour les couleurs de la boutique (Super Admin uniquement)
   * @param {string} boutiqueId - ID de la boutique
   * @param {Object} colors - Couleurs primaire et secondaire
   * @returns {Promise<Object>} Boutique avec nouvelles couleurs
   */
  updateBoutiqueColors: async (boutiqueId, colors) => {
    try {
      console.log('🎨 Mise à jour couleurs boutique:', boutiqueId, colors);
      
      const { primaryColor, secondaryColor } = colors;

      // Validation des couleurs
      if (!isValidHexColor(primaryColor)) {
        return {
          success: false,
          message: 'Couleur primaire invalide (format: #RRGGBB)'
        };
      }

      if (!isValidHexColor(secondaryColor)) {
        return {
          success: false,
          message: 'Couleur secondaire invalide (format: #RRGGBB)'
        };
      }

      // Envoyer la requête
      const response = await apiCall.put(
        endpoints.admin.boutiques.updateColors(boutiqueId),
        {
          primaryColor,
          secondaryColor
        }
      );

      if (response.success) {
        console.log('✅ Couleurs mises à jour avec succès');
        return response;
      } else {
        console.error('❌ Erreur mise à jour couleurs:', response.message);
        return response;
      }

    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour des couleurs:', error);
      return {
        success: false,
        message: 'Erreur lors de la mise à jour des couleurs'
      };
    }
  },

  /**
   * Récupérer la configuration complète d'une boutique
   * @param {string} boutiqueId - ID de la boutique
   * @returns {Promise<Object>} Configuration de la boutique
   */
  getBoutiqueConfig: async (boutiqueId) => {
    try {
      console.log('📋 Récupération config boutique:', boutiqueId);
      
      const response = await apiCall.get(endpoints.admin.boutiques.getConfig(boutiqueId));
      
      if (response.success) {
        console.log('✅ Configuration récupérée');
        return response;
      } else {
        console.error('❌ Erreur récupération config:', response.message);
        return response;
      }

    } catch (error) {
      console.error('❌ Erreur lors de la récupération de la configuration:', error);
      return {
        success: false,
        message: 'Erreur lors de la récupération de la configuration'
      };
    }
  },

  /**
   * Prévisualiser les couleurs avant application
   * @param {string} primaryColor - Couleur primaire
   * @param {string} secondaryColor - Couleur secondaire
   * @returns {Object} Styles CSS pour prévisualisation
   */
  previewColors: (primaryColor, secondaryColor) => {
    return {
      '--bs-primary': primaryColor,
      '--bs-secondary': secondaryColor,
      '--primary-rgb': hexToRgb(primaryColor),
      '--secondary-rgb': hexToRgb(secondaryColor),
      '--primary-light': `${primaryColor}20`, // 20% opacity
      '--secondary-light': `${secondaryColor}20`,
    };
  },

  

  /**
   * Helper: Formater les données pour l'affichage
   */
  formatBoutiqueForDisplay: (boutique) => {
    if (!boutique) return null;

    return {
      ...boutique,
      displayName: boutique.name || 'Boutique sans nom',
      statusLabel: boutique.status === 'ACTIVE' ? 'Active' : 'Archivée',
      statusColor: boutique.status === 'ACTIVE' ? 'success' : 'secondary',
      createdDate: boutique.createdAt ? new Date(boutique.createdAt).toLocaleDateString('fr-FR') : '',
      theme: {
        primaryColor: boutique.primaryColor || '#3b82f6',
        secondaryColor: boutique.secondaryColor || '#1d4ed8'
      }
    };
  }
};

/**
 * ✅ NOUVEAU SERVICE : boutiqueAdminService
 * Service spécialisé pour les actions admin critiques sur les boutiques
 */
export const boutiqueAdminService = {
  
  /**
   * Récupérer les données de confirmation avant suppression des ventes
   */
  getSalesDeleteConfirmation: async (boutiqueId) => {
    try {
      console.log('📊 Récupération des données de confirmation pour boutique:', boutiqueId);
      
      // Premier appel pour récupérer les données sans confirmation
      const response = await apiCall.delete(`/admin/boutiques/${boutiqueId}/sales?confirmed=false`);
      
      if (response.success) {
        console.log('✅ Données de confirmation récupérées:', response.data);
        return response;
      }
      
      return {
        success: false,
        message: response.message || 'Erreur lors de la récupération des données'
      };
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des données de confirmation:', error);
      return {
        success: false,
        message: 'Erreur de connexion'
      };
    }
  },

  /**
   * Supprimer toutes les ventes d'une boutique (confirmation requise)
   */
  deleteAllBoutiqueSales: async (boutiqueId) => {
    try {
      console.log('🗑️ Suppression de toutes les ventes de la boutique:', boutiqueId);
      
      const response = await apiCall.delete(`/admin/boutiques/${boutiqueId}/sales?confirmed=true`);
      
      if (response.success) {
        console.log('✅ Toutes les ventes supprimées:', response.data);
        return response;
      }
      
      return {
        success: false,
        message: response.message || 'Erreur lors de la suppression'
      };
    } catch (error) {
      console.error('❌ Erreur lors de la suppression des ventes:', error);
      return {
        success: false,
        message: 'Erreur de connexion'
      };
    }
  },

  /**
   * Récupérer les statistiques des ventes d'une boutique
   */
  getBoutiqueSalesStats: async (boutiqueId) => {
    try {
      console.log('📈 Récupération des stats ventes pour boutique:', boutiqueId);
      
      const response = await apiCall.get(`/admin/boutiques/${boutiqueId}/sales/stats`);
      
      if (response.success) {
        console.log('✅ Stats ventes récupérées:', response.data);
        return response;
      }
      
      return {
        success: false,
        message: response.message || 'Erreur lors de la récupération des statistiques'
      };
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des stats:', error);
      return {
        success: false,
        message: 'Erreur de connexion'
      };
    }
  },

  /**
   * Vérifier si une boutique a des ventes avant suppression
   */
  checkBoutiqueSales: async (boutiqueId) => {
    try {
      console.log('🔍 Vérification des ventes pour la boutique:', boutiqueId);
      
      const response = await apiCall.get(`/admin/boutiques/${boutiqueId}/sales/check`);
      
      if (response.success) {
        console.log('✅ Vérification terminée:', response.data);
        return response;
      }
      
      return {
        success: false,
        message: response.message || 'Erreur lors de la vérification'
      };
    } catch (error) {
      console.error('❌ Erreur lors de la vérification des ventes:', error);
      return {
        success: false,
        message: 'Erreur de connexion'
      };
    }
  },

  /**
   * Exporter les données des ventes avant suppression (optionnel)
   */
  exportBoutiqueSales: async (boutiqueId, format = 'csv') => {
    try {
      console.log('📤 Export des ventes pour la boutique:', boutiqueId, 'format:', format);
      
      const response = await apiCall.get(`/admin/boutiques/${boutiqueId}/sales/export?format=${format}`, {
        responseType: 'blob' // Pour télécharger le fichier
      });
      
      if (response.success) {
        console.log('✅ Export terminé');
        
        // Créer un lien de téléchargement
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ventes-boutique-${boutiqueId}-${new Date().toISOString().split('T')[0]}.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        return {
          success: true,
          message: 'Export terminé avec succès'
        };
      }
      
      return {
        success: false,
        message: response.message || 'Erreur lors de l\'export'
      };
    } catch (error) {
      console.error('❌ Erreur lors de l\'export des ventes:', error);
      return {
        success: false,
        message: 'Erreur de connexion'
      };
    }
  }
};

/**
 * Valider une couleur hexadécimale
 */
function isValidHexColor(color) {
  if (!color || typeof color !== 'string') {
    return false;
  }
  return /^#[0-9A-Fa-f]{6}$/.test(color.trim());
}

/**
 * Convertir hex en RGB
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? 
    `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : 
    '59, 130, 246'; // Fallback
}

/**
 * Générer une couleur plus sombre pour les hovers
 */
function darkenColor(hex, percent = 0.1) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  
  const r = Math.max(0, parseInt(result[1], 16) * (1 - percent));
  const g = Math.max(0, parseInt(result[2], 16) * (1 - percent));
  const b = Math.max(0, parseInt(result[3], 16) * (1 - percent));
  
  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}

export default boutiqueService;